// src/components/RemediationScorecardPage.js
import React, { useState, useEffect, useCallback, useId, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { BarChart3, ChevronDown, ChevronRight, ChevronsDownUp, ChevronsUpDown, Download, Pencil, Search } from "lucide-react";
import Navigation from "./Navigation";
import Card from "./Card";
import LoadingSpinner from "./LoadingSpinner";
import { useApi } from "../hooks/useApi";
import { useRBAC } from "../hooks/useRBAC";
import { buildDrillThroughUrl } from "./remediationScorecardDrillThrough";
import { activeSourceIndex, filterScorecardSources } from "./remediationScorecardView";
import { PINNED_SIDEBAR_STICKY } from "./PinnedNavigation";

const PERIOD_OPTIONS = [
    { value: "quarter", label: "Quarter" },
    { value: "month", label: "Month" },
];

const MONTH_NAMES = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];

const MAX_CATEGORY_LENGTH = 100;

// The category API rejects this display name for a blank category, so it is never suggested.
const UNCATEGORIZED = "uncategorized";

const fiscalYearLabel = (year) => `FY${String(year).slice(-2)}`;

const isValidResponse = (res) =>
    !!res && typeof res === "object" && Array.isArray(res.sources) && Array.isArray(res.periods);

// Color for the current period (PLAN D12), with the band stated in text so color is not the only cue.
const currentBand = (value) => {
    if (value < 85) return { className: "text-red-700 bg-red-100", text: "Below 85%" };
    if (value < 90) return { className: "text-orange-700 bg-orange-100", text: "85% to under 90%" };
    if (value < 100) return { className: "text-amber-700 bg-amber-100", text: "90% to under 100%" };
    return { className: "text-green-700 bg-green-100", text: "Complete" };
};

const PercentCell = ({ value, period }) => {
    const cellClass = "px-2 py-2 text-right text-sm whitespace-nowrap";
    if (period.isFuture || value === null || value === undefined) {
        return (
            <td className={`${cellClass} text-content-secondary`}>
                <span aria-hidden="true">—</span>
                <span className="sr-only">{period.isFuture ? "Period not started" : "No items to remediate"}</span>
            </td>
        );
    }
    if (period.isCurrent) {
        const band = currentBand(value);
        return (
            <td className={cellClass}>
                <span className={`inline-block rounded px-2 py-0.5 font-medium ${band.className}`} title={band.text}>
                    <span>{value}%</span>
                    <span className="sr-only">{band.text}</span>
                </span>
            </td>
        );
    }
    return (
        <td className={`${cellClass} text-content-primary`}>
            <span>{value}%</span>
            <div className="mt-1 h-1.5 w-full min-w-[3rem] rounded-full bg-theme-hover" aria-hidden="true">
                <div
                    data-testid="percent-bar"
                    className="h-1.5 rounded-full bg-indigo-500"
                    style={{ width: `${Math.min(Math.max(value, 0), 100)}%` }}
                />
            </div>
        </td>
    );
};

/** A row's category. Users who can edit get a pencil, and Reset once the row is mapped. */
const CategoryCell = ({ row, sourceKey, categoryActions }) => {
    const { canEdit, busy, options, onSave, onReset } = categoryActions;
    const listId = useId();
    const [draft, setDraft] = useState(null);
    // Enter or Escape closes the editor, and removing the input can also fire blur: only the first counts.
    const finished = useRef(false);

    const startEditing = () => {
        finished.current = false;
        setDraft(row.category);
    };

    const finish = (save) => {
        if (finished.current) return;
        finished.current = true;
        const value = draft.trim();
        setDraft(null);
        if (save && value && value !== row.category) onSave(sourceKey, row.category, value);
    };

    if (draft !== null) {
        const suggestions = (options?.[sourceKey] || []).filter((option) => option.toLowerCase() !== UNCATEGORIZED);
        return (
            <>
                <input
                    autoFocus
                    list={listId}
                    aria-label={`Category for ${row.category}`}
                    value={draft}
                    maxLength={MAX_CATEGORY_LENGTH}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") { e.preventDefault(); finish(true); }
                        if (e.key === "Escape") { e.preventDefault(); finish(false); }
                    }}
                    onBlur={() => finish(true)}
                    className="w-full min-w-[10rem] border border-border rounded-md px-2 py-1 text-sm text-content-primary bg-theme-card-solid"
                />
                <datalist id={listId}>
                    {suggestions.map((option) => <option key={option} value={option} />)}
                </datalist>
            </>
        );
    }
    return (
        <div className="flex items-center gap-2">
            <span>{row.category}</span>
            {canEdit && (
                <button
                    type="button"
                    aria-label={`Edit category ${row.category}`}
                    title="Edit category"
                    disabled={busy}
                    onClick={startEditing}
                    className="text-content-secondary hover:text-content-primary disabled:opacity-50"
                >
                    <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
            )}
            {canEdit && row.isMapped && (
                <button
                    type="button"
                    aria-label={`Reset category ${row.category}`}
                    disabled={busy}
                    onClick={() => onReset(sourceKey, row.category)}
                    className="text-xs text-indigo-600 hover:underline disabled:opacity-50"
                >
                    Reset
                </button>
            )}
        </div>
    );
};

/**
 * One table row. `source` is set on the first row of a source, for the spanning Risk Source cell.
 * `label` replaces the row's category on subtotal and grand total rows, whose API names are not shown.
 */
const ScorecardRow = ({ row, periods, source, drillSource, sourceRowSpan, label, variant = "row", sourceKey, categoryActions, collapse }) => {
    const emphasis = {
        row: "",
        subtotal: "font-semibold bg-theme-hover",
        grand: "font-bold bg-theme-hover border-t-2 border-border",
    }[variant];
    const countClass = "px-2 py-2 text-right text-sm text-content-primary";
    return (
        <tr data-source-key={source ? source.sourceKey : undefined} className={`border-t border-border ${emphasis}`}>
            {source && (
                <td rowSpan={sourceRowSpan} className="px-3 py-2 text-sm font-medium text-content-primary align-top bg-theme-card-solid">
                    {collapse ? (
                        <button
                            type="button"
                            onClick={collapse.onToggle}
                            aria-expanded={!collapse.collapsed}
                            title={collapse.collapsed ? "Show categories" : "Hide categories"}
                            className="flex items-start gap-1 text-left hover:text-indigo-700"
                        >
                            {collapse.collapsed
                                ? <ChevronRight className="h-4 w-4 mt-0.5 shrink-0 print:hidden" aria-hidden="true" />
                                : <ChevronDown className="h-4 w-4 mt-0.5 shrink-0 print:hidden" aria-hidden="true" />}
                            <span>{source.label}</span>
                        </button>
                    ) : source.label}
                </td>
            )}
            {variant === "grand" ? (
                <td colSpan={2} className="px-3 py-2 text-sm text-content-primary">{label}</td>
            ) : (
                <td className="px-3 py-2 text-sm text-content-primary">
                    {label ?? (
                        <div className="flex flex-col items-start gap-0.5">
                            <CategoryCell row={row} sourceKey={sourceKey} categoryActions={categoryActions} />
                            <Link
                                to={buildDrillThroughUrl(drillSource, row)}
                                aria-label={`View risks for ${row.category}`}
                                className="shrink-0 text-xs text-indigo-600 hover:underline"
                            >
                                View risks
                            </Link>
                        </div>
                    )}
                </td>
            )}
            <td className={countClass}>{row.itemsToRemediate}</td>
            <td className={countClass}>{row.itemsRemediated}</td>
            <td className={countClass}>{row.itemsAcceptedOrDeferred}</td>
            {periods.map((period, index) => (
                <PercentCell key={period.label} value={row.percentages?.[index]} period={period} />
            ))}
        </tr>
    );
};

/** A source's category rows and subtotal. Collapsed, only the subtotal shows, with the source beside it. */
const ScorecardSourceRows = ({ source, periods, categoryActions, collapsed, onToggle }) => {
    const allRows = source.rows || [];
    const rows = collapsed ? [] : allRows;
    const rowSpan = rows.length + 1; // its rows plus the subtotal
    const collapse = allRows.length > 0 ? { collapsed, onToggle: () => onToggle(source.sourceKey) } : null;
    return (
        <>
            {rows.map((row, index) => (
                <ScorecardRow
                    key={row.category}
                    row={row}
                    periods={periods}
                    source={index === 0 ? source : null}
                    drillSource={source}
                    sourceRowSpan={rowSpan}
                    sourceKey={source.sourceKey}
                    categoryActions={categoryActions}
                    collapse={collapse}
                />
            ))}
            <ScorecardRow
                row={source.subtotal}
                periods={periods}
                source={rows.length === 0 ? source : null}
                sourceRowSpan={rowSpan}
                label="Totals | Progress"
                variant="subtotal"
                collapse={collapse}
            />
        </>
    );
};

/** The left menu: jumps the table to a source and highlights the source scrolled to. */
const ScorecardSourceMenu = ({ sources, activeKey, onJump }) => (
    <nav aria-label="Risk sources" className={`bg-theme-card-solid rounded-lg border border-border p-4 sticky ${PINNED_SIDEBAR_STICKY} overflow-y-auto`}>
        <h2 className="text-sm font-semibold text-content-primary mb-3">Risk Sources</h2>
        <ul className="space-y-1">
            {sources.map((source) => {
                const isActive = source.sourceKey === activeKey;
                return (
                    <li key={source.sourceKey}>
                        <button
                            type="button"
                            onClick={() => onJump(source.sourceKey)}
                            title={source.label}
                            aria-current={isActive ? "true" : undefined}
                            className={`w-full text-left px-3 py-2 rounded-lg text-sm truncate ${isActive
                                ? "bg-indigo-50 text-indigo-700 font-medium"
                                : "text-content-secondary hover:bg-theme-hover"}`}
                        >
                            {source.label}
                        </button>
                    </li>
                );
            })}
        </ul>
    </nav>
);

const ScorecardTable = ({ data, sources, searchActive, isMonthView, categoryActions, scrollBoxRef, collapsedKeys, onToggleSource }) => {
    const { periods, grandTotal } = data;
    const headClass = "py-2 text-xs font-semibold text-content-secondary align-bottom";
    // The number and period columns pad less than Risk Source and Category, so the quarter view fits.
    const textHeadClass = `${headClass} px-3`;
    const numberHeadClass = `${headClass} px-2`;
    return (
        <div ref={scrollBoxRef} data-testid="scorecard-scroll" className="max-h-[calc(100vh-8.25rem)] overflow-auto print:max-h-none print:overflow-visible">
            <table className={`w-full border-collapse ${isMonthView ? "min-w-[1400px]" : "min-w-[720px]"}`}>
                <thead className="sticky top-0 z-10 bg-theme-card-solid shadow-[0_1px_0_var(--border-color)] print:static">
                    <tr>
                        <th scope="col" rowSpan={2} className={`${textHeadClass} text-left`}>Risk Source</th>
                        <th scope="col" rowSpan={2} className={`${textHeadClass} text-left`}>Category</th>
                        <th scope="col" rowSpan={2} className={`${numberHeadClass} text-right`}>Items to Remediate</th>
                        <th scope="col" rowSpan={2} className={`${numberHeadClass} text-right`}>Items Remediated</th>
                        <th scope="col" rowSpan={2} className={`${numberHeadClass} text-right`}>Items Accepted or Deferred</th>
                        <th scope="colgroup" colSpan={periods.length} className={`${numberHeadClass} text-center border-b border-border`}>
                            Remediation Progress
                        </th>
                    </tr>
                    <tr>
                        {periods.map((period) => (
                            <th key={period.label} scope="col" className={`${numberHeadClass} text-right whitespace-nowrap`}>
                                {period.label}
                                {period.isCurrent && <span className="block font-normal">to date</span>}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {sources.map((source) => (
                        <ScorecardSourceRows
                            key={source.sourceKey}
                            source={source}
                            periods={periods}
                            categoryActions={categoryActions}
                            collapsed={collapsedKeys.has(source.sourceKey)}
                            onToggle={onToggleSource}
                        />
                    ))}
                    {grandTotal && (
                        <ScorecardRow row={grandTotal} periods={periods} label={searchActive ? "Grand Totals (all sources)" : "Grand Totals"} variant="grand" />
                    )}
                </tbody>
            </table>
        </div>
    );
};

/**
 * Executive remediation progress per risk source and category. The route and nav entry are gated by
 * RemediationScorecardFeatureGuard and canAccessScorecard; the API scopes the numbers to what the
 * user can read. Write gates mirror the API: categories need CanManageOrganizationRisks and the
 * fiscal year start needs CanAdministerOrganization.
 */
const RemediationScorecardPage = () => {
    const apiCall = useApi();
    const { canManageOrganizationRisks, canAdministerOrganization } = useRBAC();
    const canEditCategories = canManageOrganizationRisks();
    const canSetFiscalYearStart = canAdministerOrganization();
    // Empty until the user picks a year: the API then answers with the current fiscal year.
    const [fiscalYear, setFiscalYear] = useState("");
    const [period, setPeriod] = useState("quarter");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [actionError, setActionError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [search, setSearch] = useState("");
    // Sources showing only their subtotal. A new search opens them all, so no match is hidden.
    const [collapsedKeys, setCollapsedKeys] = useState(() => new Set());
    const latestRequest = useRef(0);
    const searchInput = useRef(null);
    const searchActive = search.trim() !== "";
    const visibleSources = useMemo(() => filterScorecardSources(data?.sources ?? [], search), [data, search]);
    const scrollBoxRef = useRef(null);
    const [activeKey, setActiveKey] = useState(null);
    // The source last jumped to. While set, scroll events leave the highlight on it.
    const jumpTarget = useRef(null);
    const showSourceNav = !loading && !error && !!data && visibleSources.length > 0;

    const updateActiveSource = useCallback(() => {
        const box = scrollBoxRef.current;
        if (!box) return;
        const starts = Array.from(box.querySelectorAll("tr[data-source-key]"));
        if (starts.length === 0) {
            setActiveKey(null);
            return;
        }
        const headerHeight = box.querySelector("thead")?.offsetHeight ?? 0;
        const atBottom = box.scrollHeight > box.clientHeight && box.scrollTop + box.clientHeight >= box.scrollHeight - 1;
        const index = activeSourceIndex(starts.map((row) => row.offsetTop), box.scrollTop, headerHeight, atBottom);
        setActiveKey(starts[index].dataset.sourceKey);
    }, []);

    // Before the tracking effect below, which reads the position. Added spaces don't change the search.
    const searchTerm = search.trim();
    useEffect(() => {
        if (scrollBoxRef.current) scrollBoxRef.current.scrollTop = 0;
        setCollapsedKeys((keys) => (keys.size === 0 ? keys : new Set()));
    }, [searchTerm]);

    useEffect(() => {
        // The visible sources or the period changed, so a held jump no longer applies.
        jumpTarget.current = null;
        if (!showSourceNav || !scrollBoxRef.current) return undefined;
        const box = scrollBoxRef.current;
        let scheduled = false;
        let frameId = 0;
        const onScroll = () => {
            if (scheduled) return;
            scheduled = true;
            frameId = requestAnimationFrame(() => {
                scheduled = false;
                if (jumpTarget.current === null) updateActiveSource();
            });
        };
        // Only the person's own input releases a jump: the jump's animation scrolls the box too.
        const releaseJump = () => { jumpTarget.current = null; };
        const userInputs = ["wheel", "touchmove", "pointerdown", "keydown"];
        box.addEventListener("scroll", onScroll, { passive: true });
        userInputs.forEach((name) => box.addEventListener(name, releaseJump, { passive: true }));
        updateActiveSource();
        return () => {
            box.removeEventListener("scroll", onScroll);
            userInputs.forEach((name) => box.removeEventListener(name, releaseJump));
            if (scheduled) cancelAnimationFrame(frameId);
        };
    }, [showSourceNav, visibleSources, period, collapsedKeys, updateActiveSource]);

    const fetchScorecard = useCallback(async () => {
        const request = ++latestRequest.current;
        setLoading(true);
        setError(false);
        setActionError(null);
        try {
            const res = await apiCall("GET", `/RemediationScorecard?fiscalYear=${fiscalYear}&period=${period}`);
            if (request !== latestRequest.current) return;
            if (!isValidResponse(res)) throw new Error("Unexpected scorecard response");
            setData(res);
        } catch {
            if (request === latestRequest.current) setError(true);
        } finally {
            if (request === latestRequest.current) setLoading(false);
        }
    }, [apiCall, fiscalYear, period]);

    useEffect(() => { fetchScorecard(); }, [fetchScorecard]);

    // Year and period stay disabled during a save, so `reload` cannot run with an outdated selection.
    const runSave = async (request, fallbackMessage, reload = fetchScorecard) => {
        setActionError(null);
        setSaving(true);
        try {
            await request();
        } catch (err) {
            setActionError(err?.response?.data?.message || fallbackMessage);
            return;
        } finally {
            setSaving(false);
        }
        reload();
    };

    const saveCategory = (sourceKey, fromCategory, toCategory) => runSave(
        () => apiCall("PUT", "/RemediationScorecard/category", { sourceKey, fromCategory, toCategory }),
        "Unable to save the category.",
    );

    const resetCategory = (sourceKey, category) => runSave(
        () => apiCall(
            "DELETE",
            `/RemediationScorecard/category?sourceKey=${encodeURIComponent(sourceKey)}&category=${encodeURIComponent(category)}`,
        ),
        "Unable to reset the category.",
    );

    // Periods move with the start month, so the selected year may no longer apply: go back to the
    // response's year. Clearing a selected year reloads through the effect.
    const saveFiscalYearStart = (month) => runSave(
        () => apiCall("PUT", "/RemediationScorecard/fiscal-year-start", { month }),
        "Unable to save the fiscal year start.",
        fiscalYear ? () => setFiscalYear("") : fetchScorecard,
    );

    const handleExport = async () => {
        const year = fiscalYear || data.fiscalYear;
        setActionError(null);
        setExporting(true);
        try {
            const blob = await apiCall("GET", `/RemediationScorecard/export?fiscalYear=${year}&period=${period}`, null, false, "blob");
            if (!blob) return; // useApi is redirecting to sign in
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `remediation-scorecard-FY${year}.xlsx`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            setActionError(err?.response?.data?.message || "Unable to export the remediation scorecard.");
        } finally {
            setExporting(false);
        }
    };

    const toggleSource = (key) => setCollapsedKeys((keys) => {
        const next = new Set(keys);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });

    // Sources with no category rows have nothing to collapse.
    const collapsibleKeys = visibleSources.filter((source) => (source.rows || []).length > 0).map((source) => source.sourceKey);
    const allCollapsed = collapsibleKeys.length > 0 && collapsibleKeys.every((key) => collapsedKeys.has(key));
    const toggleAll = () => setCollapsedKeys(allCollapsed ? new Set() : new Set(collapsibleKeys));

    const clearSearch = () => {
        setSearch("");
        searchInput.current?.focus();
    };

    const jumpToSource = (key) => {
        const box = scrollBoxRef.current;
        if (!box) return;
        // Compared in JS, not with a CSS selector: keys like GRC:TPRM contain ":".
        const row = Array.from(box.querySelectorAll("tr[data-source-key]")).find((r) => r.dataset.sourceKey === key);
        if (!row) return;
        box.scrollIntoView({ block: "start", behavior: "smooth" });
        const headerHeight = box.querySelector("thead")?.offsetHeight ?? 0;
        const maxTop = Math.max(box.scrollHeight - box.clientHeight, 0);
        const targetTop = Math.min(Math.max(row.offsetTop - headerHeight, 0), maxTop);
        jumpTarget.current = key;
        setActiveKey(key);
        box.scrollTo({ top: targetTop, behavior: "smooth" });
    };

    const renderToolbar = () => (
        <div data-testid="scorecard-toolbar" className="flex flex-wrap items-center gap-3 mb-4 print:hidden">
            <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-content-secondary" aria-hidden="true" />
                <input
                    ref={searchInput}
                    type="search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    aria-label="Search the scorecard"
                    placeholder="Search categories or sources"
                    className="w-full pl-9 pr-2 py-2 text-sm border border-border rounded-lg bg-theme-card-solid text-content-primary"
                />
            </div>
            {showSourceNav && collapsibleKeys.length > 0 && (
                <button
                    type="button"
                    onClick={toggleAll}
                    className="inline-flex items-center gap-2 px-3 py-1.5 border border-border rounded-md text-sm text-content-primary hover:bg-theme-hover whitespace-nowrap"
                >
                    {allCollapsed
                        ? <ChevronsUpDown className="h-4 w-4" aria-hidden="true" />
                        : <ChevronsDownUp className="h-4 w-4" aria-hidden="true" />}
                    {allCollapsed ? "Expand all" : "Collapse all"}
                </button>
            )}
            {showSourceNav && (
                // basis-full below sm: with a zero basis this stays beside the search box and leaves the select no width.
                <label className="lg:hidden flex min-w-0 max-w-full flex-1 basis-full sm:basis-auto items-center gap-2 whitespace-nowrap text-sm text-content-secondary">
                    Jump to source
                    <select
                        value={activeKey ?? ""}
                        onChange={(e) => jumpToSource(e.target.value)}
                        className="min-w-0 max-w-full flex-1 truncate border border-border rounded-md px-2 py-1.5 text-sm text-content-primary bg-theme-card-solid"
                    >
                        {visibleSources.map((source) => (
                            <option key={source.sourceKey} value={source.sourceKey}>{source.label}</option>
                        ))}
                    </select>
                </label>
            )}
        </div>
    );

    const renderBody = () => {
        if (loading) return <LoadingSpinner className="!min-h-0 !bg-transparent py-12" />;
        if (error) {
            return (
                <div role="alert" className="py-8 text-center">
                    <p className="text-sm text-red-600 mb-3">Unable to load the remediation scorecard.</p>
                    <button
                        type="button"
                        onClick={fetchScorecard}
                        className="px-3 py-1.5 border border-border rounded-md text-sm text-content-primary hover:bg-theme-hover"
                    >
                        Retry
                    </button>
                </div>
            );
        }
        if (data.sources.length === 0) {
            return <p className="py-8 text-center text-sm text-content-secondary">No risks to report yet.</p>;
        }
        // One toolbar position for both branches, so the input keeps focus when typing empties the table.
        return (
            <>
                {renderToolbar()}
                {visibleSources.length === 0 ? (
                    <div role="status" className="py-8 text-center">
                        <p className="text-sm text-content-secondary mb-3 break-words">
                            {`No categories match "${search.trim()}".`}
                        </p>
                        <button
                            type="button"
                            onClick={clearSearch}
                            className="px-3 py-1.5 border border-border rounded-md text-sm text-content-primary hover:bg-theme-hover"
                        >
                            Clear search
                        </button>
                    </div>
                ) : (
                    <ScorecardTable
                        data={data}
                        sources={visibleSources}
                        searchActive={searchActive}
                        isMonthView={period === "month"}
                        scrollBoxRef={scrollBoxRef}
                        collapsedKeys={collapsedKeys}
                        onToggleSource={toggleSource}
                        categoryActions={{
                            canEdit: canEditCategories,
                            busy: saving,
                            options: data.categoryOptions,
                            onSave: saveCategory,
                            onReset: resetCategory,
                        }}
                    />
                )}
            </>
        );
    };

    return (
        <div className="min-h-screen bg-theme-primary">
            <Navigation />
            {/* No max-w-7xl cap: the page grows with the window so wide monitors show every month without scrolling. */}
            <div className="px-4 sm:px-6 lg:px-8 py-8">
                <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                    <div className="flex items-center gap-3">
                        <BarChart3 className="h-7 w-7 text-indigo-600" />
                        <h1 className="text-2xl font-bold text-content-primary">Remediation Scorecard</h1>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                        {data && (
                            <label className="flex items-center gap-2 text-sm text-content-secondary">
                                Fiscal year
                                <select
                                    value={fiscalYear || data.fiscalYear}
                                    onChange={(e) => {
                                        setFiscalYear(e.target.value);
                                        setSearch("");
                                    }}
                                    disabled={saving}
                                    className="border border-border rounded-md px-2 py-1.5 text-sm text-content-primary bg-theme-card-solid"
                                >
                                    {(data.availableFiscalYears || []).map((year) => (
                                        <option key={year} value={year}>{fiscalYearLabel(year)}</option>
                                    ))}
                                </select>
                            </label>
                        )}
                        {data && canSetFiscalYearStart && (
                            <label className="flex items-center gap-2 text-sm text-content-secondary">
                                Fiscal year starts in
                                <select
                                    value={data.fiscalYearStartMonth}
                                    onChange={(e) => saveFiscalYearStart(Number(e.target.value))}
                                    disabled={saving || loading}
                                    className="border border-border rounded-md px-2 py-1.5 text-sm text-content-primary bg-theme-card-solid"
                                >
                                    {MONTH_NAMES.map((name, index) => (
                                        <option key={name} value={index + 1}>{name}</option>
                                    ))}
                                </select>
                            </label>
                        )}
                        <div role="group" aria-label="Period" className="inline-flex rounded-md border border-border overflow-hidden">
                            {PERIOD_OPTIONS.map((option) => (
                                <button
                                    key={option.value}
                                    type="button"
                                    aria-pressed={period === option.value}
                                    onClick={() => {
                                        if (option.value !== period) setSearch("");
                                        setPeriod(option.value);
                                    }}
                                    disabled={saving}
                                    className={`px-3 py-1.5 text-sm ${period === option.value
                                        ? "bg-indigo-600 text-white"
                                        : "text-content-secondary hover:bg-theme-hover"}`}
                                >
                                    {option.label}
                                </button>
                            ))}
                        </div>
                        {data && (
                            <button
                                type="button"
                                onClick={handleExport}
                                disabled={saving || exporting || loading || error}
                                className="inline-flex items-center gap-2 px-3 py-1.5 border border-border rounded-md text-sm text-content-primary hover:bg-theme-hover disabled:opacity-50"
                            >
                                <Download className="h-4 w-4" aria-hidden="true" />
                                Export to Excel
                            </button>
                        )}
                    </div>
                </div>
                {actionError && (
                    <p role="alert" className="mb-4 text-sm text-red-600">{actionError}</p>
                )}
                {/* No items-start: the menu's column must stretch to the table's height for sticky to work. */}
                <div className="flex gap-6">
                    {showSourceNav && (
                        <div data-testid="scorecard-source-nav" className="hidden lg:block w-56 flex-shrink-0 print:hidden">
                            <ScorecardSourceMenu sources={visibleSources} activeKey={activeKey} onJump={jumpToSource} />
                        </div>
                    )}
                    <div className="flex-1 min-w-0">
                        <Card>{renderBody()}</Card>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RemediationScorecardPage;
